import React, { useState } from "react";
import { Card, Form, Input, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserData, setUserLoggedIn } from "../../redux/authSlice.ts";
import {
  signIn,
  confirmSignIn,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Amplify } from "aws-amplify";
import awscognitpool from "../../global/awscognitpool.ts";
import userService from "../../services/user-service.ts";
import Constant from "../../global/Constant.ts";
import { setUserDetails } from "../../redux/userSlice.ts";
import logo from "../../assets/images/logo.svg";
import { decryptData } from "../../utils/decryptData.ts"; // adjust path if needed
Amplify.configure(awscognitpool);
const LoginPage: React.FC = () => {
  const [adminloginform] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [userSession, setUserSession] = useState(null);
  const [additionalFieldLabel, setAdditionalFieldLabel] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (values) => {
    const user = await signIn({
      username: values.email,
      password: values.password,
    });

    if (user?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
      setAdditionalFieldLabel("Confirmation Code");
      setUserSession({ ...user, username: values.email });
      return;
    }

    if (
      user?.nextStep?.signInStep ===
      "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      setAdditionalFieldLabel("New Password");
      setUserSession(user);
      return;
    }

    await handleSuccessfulLogin(user, values.email);
  };

  const handleSuccessfulLogin = async (user, email) => {
    const loggedUser = await getCurrentUser();
    const session = await fetchAuthSession();

    let userAttributes = {};
    if (session?.tokens) {
      userAttributes = {
        ...loggedUser,
        groups: session.tokens.idToken.payload["cognito:groups"] || [],
      };
    }

    if (
      userAttributes.groups?.some((group) =>
        ["group", "individual"].includes(group)
      )
    ) {
      setError("Access Denied: users cannot log in.");
      await signOut();
      return;
    }

    const userDetailsEncrypted = await userService.getUserDetailsByEmail(
      Constant.userdetails
    );
    const userDetails = await decryptData(
      userDetailsEncrypted?.data,
      process.env.REACT_APP_ENCRYPTION_KEY!
    );

    if (!userDetails.error) {
      dispatch(setUserDetails(userDetails));
    }

    dispatch(setUserLoggedIn());
    dispatch(setUserData(userAttributes));
    localStorage.setItem("sessionStartTime", new Date().getTime().toString());
    navigate("/");
  };

  const handleConfirmation = async (values) => {
    if (userSession?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
      await confirmSignUp({
        username: userSession.username,
        confirmationCode: values.newpassword,
      });
    } else {
      await confirmSignIn({
        user: userSession,
        challengeResponse: values.newpassword,
      });
    }
    setUserSession(null);
    await signOut();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = await adminloginform.validateFields();

      if (!values.newpassword) {
        await handleSignIn(values);
      } else {
        await handleConfirmation(values);
      }
    } catch (err) {
      setError(err.message.replace("username", "email"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vm-login">
      <Card className="vm-login__card vm__min-w-xs">
        <div className="vm__login__card__logo vm__text-center vm__mb-3">
          <img src={logo} alt="Vitromed Logo" />
        </div>
        {/* <Title level={3} className=" vm__text-center vm__mb-3">
          Admin Login
        </Title> */}
        <Form layout="vertical" form={adminloginform} onFinish={onFinish}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email!" },
              {
                type: "email",
                message: "Please enter a valid email address!",
              },
            ]}
          >
            <Input placeholder="Enter your email" autoComplete="off" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please enter your password!" }]}
          >
            <Input.Password
              placeholder="Enter your password"
              autoComplete="new-password"
            />
          </Form.Item>

          {userSession && (
            <Form.Item
              label={additionalFieldLabel || "Set New Password"}
              name="newpassword"
              rules={[
                {
                  required: true,
                  message: "Please enter your " + additionalFieldLabel,
                },
              ]}
              className="vm__mb-2"
            >
              <Input.Password
                placeholder={"Enter your " + additionalFieldLabel}
              />
            </Form.Item>
          )}

          <Form.Item className="mb-sm vm__mb-0">
            <Button type="primary" htmlType="submit" block loading={loading}>
              Login
            </Button>
          </Form.Item>

          {error && (
            <Form.Item className="mb-sm vm__mb-0">
              <div style={{ color: "red" }}>
                {error || "Something went wrong. Please try again."}
              </div>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;
